import { cleanObject } from '@shared/utils';
import getPieveCompanyIdForRequest from '@/lib/auth/getPieveCompanyIdForRequest.js';
import { get, post, put } from '@/redux/requests/api.js';
import collab from '@/redux/requests/collab.js';

export const getWorkflowByThreadId = ({ threadId, version = 'draft' }, callback) => {
  return get('/v1/pieve/workflows', {
    action: 'GET_THREAD_WORKFLOW_DRAFT',
    loader: 'getWorkflowByThreadId',
    callback,
    meta: { threadId },
    query: { threadId, version, detail: 'true' }
  });
};

export const listWorkflows = ({ limit = 80, includeArchived = false } = {}, callback) => {
  const companyId = String(getPieveCompanyIdForRequest() || '').trim() || null;

  return get('/v1/pieve/workflows', {
    action: 'GET_WORKFLOWS',
    loader: 'getWorkflows',
    callback,
    meta: {
      companyId
    },
    query: cleanObject(
      {
        limit,
        includeArchived
      },
      { strict: true }
    )
  });
};

export const getWorkflow = ({ workflowId, version = 'draft', includeValidationSummary = undefined } = {}, callback) => {
  const normalizedWorkflowId = String(workflowId || '').trim();
  const normalizedVersion = String(version || '').trim() || 'draft';
  const companyId = String(getPieveCompanyIdForRequest() || '').trim() || null;

  return get(`/v1/pieve/workflows/${encodeURIComponent(normalizedWorkflowId)}`, {
    action: 'GET_WORKFLOW',
    loader: 'getWorkflow',
    callback,
    meta: {
      companyId,
      workflowId: normalizedWorkflowId,
      workflowVersion: normalizedVersion
    },
    query: cleanObject(
      {
        version: normalizedVersion,
        includeValidationSummary
      },
      { strict: true }
    )
  });
};

export const listWorkflowVersions = ({ workflowId, limit = 30, offset = undefined } = {}, callback) => {
  const normalizedWorkflowId = String(workflowId || '').trim();
  const companyId = String(getPieveCompanyIdForRequest() || '').trim() || null;

  return get(`/v1/pieve/workflows/${encodeURIComponent(normalizedWorkflowId)}/versions`, {
    action: 'GET_WORKFLOW_VERSIONS',
    loader: 'getWorkflowVersions',
    callback,
    meta: {
      companyId,
      workflowId: normalizedWorkflowId
    },
    query: cleanObject(
      {
        limit,
        offset
      },
      { strict: true }
    )
  });
};

export const listWorkflowRuns = ({ workflowId, threadId = undefined, sourceType = undefined, state = undefined, environmentId = undefined, startDate = undefined, endDate = undefined, timezone = undefined, signal = undefined } = {}, callback) => {
  const normalizedWorkflowId = String(workflowId || '').trim();
  const normalizedThreadId = String(threadId || '').trim() || null;
  const companyId = String(getPieveCompanyIdForRequest() || '').trim() || null;

  return get(`/v1/pieve/workflows/${encodeURIComponent(normalizedWorkflowId)}/runs`, {
    action: 'GET_WORKFLOW_RUNS',
    loader: 'getWorkflowRuns',
    callback,
    signal,
    meta: {
      companyId,
      workflowId: normalizedWorkflowId,
      threadId: normalizedThreadId
    },
    query: cleanObject(
      {
        sourceType,
        state,
        environmentId,
        startDate,
        endDate,
        timezone
      },
      { strict: true }
    )
  });
};

// The next page of run history below `cursor` (the last run id held), in the same date window.
export const listOlderWorkflowRuns = ({ workflowId, cursor, environmentId = undefined, startDate = undefined, endDate = undefined, timezone = undefined } = {}) => {
  return get(`/v1/pieve/workflows/${encodeURIComponent(workflowId)}/runs`, {
    action: 'GET_OLDER_WORKFLOW_RUNS',
    loader: 'getOlderWorkflowRuns',
    meta: { workflowId, cursor },
    query: cleanObject({ cursor, environmentId, startDate, endDate, timezone }, { strict: true })
  });
};

export const searchWorkflowRuns = ({ workflowId, search, requestKey, requestId, environmentId, startDate, endDate, timezone, signal }) => {
  return get(`/v1/pieve/workflows/${encodeURIComponent(workflowId)}/runs`, {
    action: 'SEARCH_WORKFLOW_RUNS',
    signal,
    meta: { requestKey, requestId },
    query: cleanObject({ search, environmentId, startDate, endDate, timezone }, { strict: true })
  });
};

export const getWorkflowRunDailyCounts = ({ workflowId, threadId = undefined, startDate = undefined, endDate = undefined, timezone = undefined, signal = undefined } = {}, callback) => {
  const normalizedWorkflowId = String(workflowId || '').trim();
  const normalizedThreadId = String(threadId || '').trim() || null;

  return get(`/v1/pieve/workflows/${encodeURIComponent(normalizedWorkflowId)}/runs/daily-counts`, {
    action: 'GET_WORKFLOW_RUN_DAILY_COUNTS',
    loader: 'getWorkflowRunDailyCounts',
    callback,
    signal,
    meta: {
      workflowId: normalizedWorkflowId,
      threadId: normalizedThreadId
    },
    query: cleanObject(
      {
        startDate,
        endDate,
        timezone
      },
      { strict: true }
    )
  });
};

// The changes one run made, from the governance ledger. Fetched with the run's logs.
export const listWorkflowRunOperations = ({ workflowId, workflowRunId } = {}) => {
  return get(`/v1/pieve/workflows/${encodeURIComponent(workflowId)}/runs/${encodeURIComponent(workflowRunId)}/operations`, {
    action: 'GET_WORKFLOW_RUN_OPERATIONS',
    loader: 'getWorkflowRunOperations',
    meta: { workflowRunId }
  });
};

// Every change the workflow's runs made in the date window: the workflow detail page's Recent activity table.
export const listWorkflowOperations = ({ workflowId, startDate = undefined, endDate = undefined, timezone = undefined } = {}) => {
  return get(`/v1/pieve/workflows/${encodeURIComponent(workflowId)}/operations`, {
    action: 'GET_WORKFLOW_OPERATIONS',
    loader: 'getWorkflowOperations',
    query: cleanObject({ startDate, endDate, timezone }, { strict: true })
  });
};

export const getWorkflowRecentActivityChartDailyCounts = ({ workflowId, startDate = undefined, endDate = undefined, timezone = undefined } = {}) => {
  return get(`/v1/pieve/workflows/${encodeURIComponent(workflowId)}/operations/daily-counts`, {
    action: 'GET_WORKFLOW_RECENT_ACTIVITY_CHART_DAILY_COUNTS',
    loader: 'getWorkflowRecentActivityChartDailyCounts',
    query: cleanObject({ startDate, endDate, timezone }, { strict: true })
  });
};

export const getWorkflowRunTimeMachine = ({ workflowId, workflowRunId } = {}, callback) => {
  const normalizedWorkflowId = String(workflowId || '').trim();
  const normalizedWorkflowRunId = String(workflowRunId || '').trim();

  return get(`/v1/pieve/workflows/${encodeURIComponent(normalizedWorkflowId)}/runs/${encodeURIComponent(normalizedWorkflowRunId)}/time-machine`, {
    action: 'GET_WORKFLOW_RUN_TIME_MACHINE',
    loader: 'getWorkflowRunTimeMachine',
    meta: {
      workflowId: normalizedWorkflowId,
      workflowRunId: normalizedWorkflowRunId
    },
    callback
  });
};

/* Cancel an in-flight run. No reducer action and no optimistic write: the editor already polls the
   run list every second while a run is active, so the new status arrives through the one owner of
   that data. The event bump is what makes it arrive immediately instead of on the next tick. */
export const cancelWorkflowRun = ({ workflowId, workflowRunId }) => {
  return post(`/v1/pieve/workflows/${encodeURIComponent(workflowId)}/runs/${encodeURIComponent(workflowRunId)}/cancel`, {
    eventKey: 'workflowRun'
  });
};

// `test` ({ revision }) makes the run the workflow's stored test at the revision the Test window shows. `environmentRef` is
// the environment the run binds; omitted, the server takes the workflow's own (and refuses to pick among several).
export const runWorkflow = ({ workflowId, threadId = undefined, requestId = undefined, runtime = undefined, test = undefined, environmentRef = undefined } = {}, callback) => {
  return post('/v1/pieve/workflows/tools/run_workflow', {
    action: 'RUN_WORKFLOW',
    eventKey: 'workflowRun',
    // threadId rides in meta so the auto-dispatched RUN_WORKFLOW_OPTIMISTIC can scope the
    // optimistic run to the active editor thread (the run body only carries workflowId).
    meta: { threadId: String(threadId || '').trim() || null },
    callback,
    data: cleanObject(
      {
        workflowId,
        requestId,
        runtime,
        test,
        environmentRef
      },
      { strict: true }
    )
  });
};

// Runs one step from the Test window, as built ({}) or with the person's settings ({ config }), in the environment the
// window says it runs in. The press is the person's decision to run it, writes included; the result is returned, not
// saved as the step's output.
export const runWorkflowTestStep = ({ workflowId, stepId, test = {}, environmentRef = undefined } = {}) => {
  return post('/v1/pieve/workflows/tools/workflow_step_run', {
    loader: 'runWorkflowTestStep',
    data: { workflowId, stepId, test, environmentRef }
  });
};

// What the Test window needs before anything runs: each action step's declared operation and each top-level
// loop's candidate items, resolved on the server from the source step's full saved output.
export const getWorkflowTestSetup = ({ workflowId } = {}) => {
  const normalizedWorkflowId = String(workflowId || '').trim();

  return get(`/v1/pieve/workflows/${encodeURIComponent(normalizedWorkflowId)}/test-setup`, {
    action: 'GET_WORKFLOW_TEST_SETUP',
    loader: 'getWorkflowTestSetup',
    meta: { workflowId: normalizedWorkflowId }
  });
};

// Saves the person's edits in the Test window as the workflow's test (draft meta.test), from the revision they started
// on: the server refuses them when the test changed since. The stored test, with its new revision, comes back.
export const saveWorkflowTest = ({ workflowId, steps, baseRevision } = {}) => {
  const normalizedWorkflowId = String(workflowId || '').trim();

  return put(`/v1/pieve/workflows/${encodeURIComponent(normalizedWorkflowId)}/test`, {
    action: 'PUT_WORKFLOW_TEST',
    loader: 'saveWorkflowTest',
    data: { steps: steps || {}, baseRevision },
    meta: { workflowId: normalizedWorkflowId }
  });
};

// Takes the picked unpublished changes out of the draft, each step back to how the published version has it. Stamps
// workflowDraftUpdated, so the draft, and with it the change count, refetches.
export const discardWorkflowDraftChanges = ({ workflowId, stepIds } = {}) => {
  return post(`/v1/pieve/workflows/${encodeURIComponent(workflowId)}/draft/discard`, {
    action: 'DISCARD_WORKFLOW_DRAFT_CHANGES',
    loader: 'discardWorkflowDraftChanges',
    eventKey: 'workflowDraftUpdated',
    data: { stepIds }
  });
};

export const publishWorkflow = ({ workflowId, requestId = undefined, allowInvalid = undefined, note = undefined } = {}, callback) => {
  const normalizedWorkflowId = String(workflowId || '').trim();

  return post(`/v1/pieve/workflows/${encodeURIComponent(normalizedWorkflowId)}/publish`, {
    loader: 'publishWorkflow',
    eventKey: 'workflowPublished',
    callback,
    data: cleanObject(
      {
        requestId,
        allowInvalid,
        note
      },
      { strict: true }
    )
  });
};

// Publishes an earlier version again as the next version. Same event key as publish: the draft, the version line, the
// history and the published snapshot all refetch from it.
export const restoreWorkflowVersion = ({ workflowId, version, note = undefined } = {}, callback) => {
  return post(`/v1/pieve/workflows/${encodeURIComponent(workflowId)}/versions/${encodeURIComponent(version)}/restore`, {
    loader: 'restoreWorkflowVersion',
    eventKey: 'workflowPublished',
    callback,
    data: cleanObject({ note }, { strict: true })
  });
};

// Per-app connection health for every app this thread's workflow involves — the draft's steps once it
// exists, the plan's declared apps before that. Keyed by thread because that is the one id both phases
// have, and the server picks the source from whether a draft exists rather than the renderer guessing.
//
// Computed server-side because two of its facts are not derivable in the renderer: whether an app needs a
// credential at all (its `authentication` array is absent from app metadata) and whether an org credential
// the executor can see exists (the viewer's connection list gates those behind role assignment).
export const getThreadAppConnectionStatus = ({ threadId } = {}) => {
  const normalizedThreadId = String(threadId || '').trim();

  return get(`/v1/pieve/threads/${encodeURIComponent(normalizedThreadId)}/app-connection-status`, {
    action: 'GET_THREAD_APP_CONNECTION_STATUS',
    loader: 'getThreadAppConnectionStatus',
    meta: { threadId: normalizedThreadId }
  });
};

// The thread's actions-needed list. Thread-keyed like the connection status above it, and for the same
// reason: during planning there is no workflow to key on, and planning is where most blockers are found.
export const getThreadActionsNeeded = ({ threadId } = {}) => {
  const normalizedThreadId = String(threadId || '').trim();

  return get(`/v1/pieve/threads/${encodeURIComponent(normalizedThreadId)}/actions`, {
    action: 'GET_THREAD_ACTIONS',
    loader: 'getThreadActionsNeeded',
    meta: { threadId: normalizedThreadId }
  });
};

/*
 * One row of the actions-needed list changing state — done, waived, deleted (`withdrawn`), or back to
 * open — or its annotations. Same thread key as the read above.
 *
 * `null` is a real value here — clearing a due date or a waiting-on is a `null`, not an omission — so
 * only `undefined` is stripped.
 */
export const updateThreadActionNeeded = ({ threadId, actionKey, status = undefined, answer = undefined, waitingOn = undefined, dueBy = undefined } = {}) => {
  return put(`/v1/pieve/threads/${encodeURIComponent(String(threadId || '').trim())}/actions/${encodeURIComponent(String(actionKey || '').trim())}`, {
    action: 'UPDATE_THREAD_ACTIONS',
    loader: 'updateThreadActionNeeded',
    // The response carries the whole list back, so answering a row files the same shape the read does.
    meta: { threadId: String(threadId || '').trim() },
    data: cleanObject({ status, answer, waitingOn, dueBy }, { strict: true })
  });
};

// A person adding an action of their own. Files the whole list back, like the read.
export const createThreadActionNeeded = ({ threadId, title, explanation, recommendedAnswer, resolution } = {}) => {
  return post(`/v1/pieve/threads/${encodeURIComponent(String(threadId || '').trim())}/actions`, {
    action: 'UPDATE_THREAD_ACTIONS',
    loader: 'createThreadActionNeeded',
    meta: { threadId: String(threadId || '').trim() },
    data: { title, explanation, recommendedAnswer, resolution }
  });
};

// One action's words and answer control rewritten, or `restore: true` for the wording Pieve filed.
export const updateThreadActionWording = ({ threadId, actionKey, restore = undefined, title = undefined, explanation = undefined, recommendedAnswer = undefined, resolution = undefined } = {}) => {
  return put(`/v1/pieve/threads/${encodeURIComponent(String(threadId || '').trim())}/actions/${encodeURIComponent(String(actionKey || '').trim())}/wording`, {
    action: 'UPDATE_THREAD_ACTIONS',
    loader: 'updateThreadActionWording',
    meta: { threadId: String(threadId || '').trim() },
    data: restore ? { restore: true } : { title, explanation, recommendedAnswer, resolution }
  });
};

export const getWorkflowConnections = ({ workflowId } = {}) => {
  const normalizedWorkflowId = String(workflowId || '').trim();

  return get(`/v1/pieve/workflows/${encodeURIComponent(normalizedWorkflowId)}/connections`, {
    action: 'GET_WORKFLOW_CONNECTIONS',
    loader: 'getWorkflowConnections',
    meta: { workflowId: normalizedWorkflowId }
  });
};

// The route's other `outputType`, `full`, exists for the agent tool that shares this service. The renderer
// only ever renders the truncated head, so it sends no query at all and takes the route's default.
export const getWorkflowStepOutput = ({ workflowId, stepId } = {}) => {
  return get(`/v1/pieve/workflows/${encodeURIComponent(workflowId)}/steps/${encodeURIComponent(stepId)}/output`, {
    action: 'GET_WORKFLOW_STEP_OUTPUT',
    loader: 'getWorkflowStepOutput',
    meta: { stepId }
  });
};

export const updateWorkflow = ({ workflowId, name, description, active } = {}) => {
  return put(`/v1/pieve/workflows/${encodeURIComponent(workflowId)}`, {
    action: 'UPDATE_WORKFLOW',
    loader: 'updateWorkflow',
    // workflowId rides in meta so the auto-dispatched UPDATE_WORKFLOW_OPTIMISTIC can
    // target the record (the id is in the URL, not the request body).
    meta: { workflowId: String(workflowId || '').trim() },
    data: cleanObject({ name, description, active }, { strict: true })
  });
};

/* A person's own edit to one step, as a JSON merge patch (null deletes a key), through the collab batch: edits to
   the same step inside one flush window ship as one write. The draft overlays the patch while it is in flight
   (workflowStepPendingEdits) and the response carries the draft as stored, which replaces the confirmed copy. */
export const updateWorkflowStep = ({ workflowId, stepId, patch } = {}) => {
  const normalizedWorkflowId = String(workflowId || '').trim();
  const normalizedStepId = String(stepId || '').trim();

  return collab.put('workflowStep', {
    resourceId: normalizedStepId,
    action: 'UPDATE_WORKFLOW_STEP',
    loader: 'updateWorkflowStep',
    meta: { workflowId: normalizedWorkflowId, stepId: normalizedStepId },
    data: { workflowId: normalizedWorkflowId, patch }
  });
};

// Every webhook setting through one endpoint; omitted fields are left unchanged. When a secret is minted
// the response carries it in plaintext for a one-time reveal, so the caller reads it from the callback
// rather than from Redux — it is deliberately never stored.
export const updateWorkflowWebhook = ({ workflowId, active = undefined, responseMode = undefined, rotateSlug = undefined, secretEnabled = undefined } = {}, callback) => {
  return put(`/v1/pieve/workflows/${encodeURIComponent(String(workflowId || '').trim())}/webhook`, {
    loader: 'updateWorkflowWebhook',
    callback,
    data: cleanObject({ active, responseMode, rotateSlug, secretEnabled }, { strict: true })
  });
};

export const getWorkflowWebhookSamples = ({ workflowId } = {}) => {
  return get(`/v1/pieve/workflows/${encodeURIComponent(String(workflowId || '').trim())}/webhook/samples`, {
    action: 'GET_WORKFLOW_WEBHOOK_SAMPLES',
    loader: 'getWorkflowWebhookSamples',
    meta: { workflowId: String(workflowId || '').trim() }
  });
};

export const replayWorkflowWebhookDelivery = ({ workflowId, workflowRunId } = {}) => {
  return post(`/v1/pieve/workflows/${encodeURIComponent(String(workflowId || '').trim())}/webhook/samples/${encodeURIComponent(String(workflowRunId || '').trim())}/replay`, {
    loader: 'replayWorkflowWebhookDelivery',
    eventKey: 'workflowRunStarted',
    data: {}
  });
};

/* The step's standing Pieve task — idempotent get-or-create, so the POST returns 200. The ids
   ride in meta because the optimistic action needs them before there is a response. */
export const getOrCreateWorkflowStepThread = ({ workflowId, stepId } = {}, callback) => {
  const normalizedWorkflowId = String(workflowId || '').trim();
  const normalizedStepId = String(stepId || '').trim();

  return post(`/v1/pieve/workflows/${encodeURIComponent(normalizedWorkflowId)}/steps/${encodeURIComponent(normalizedStepId)}/thread`, {
    action: 'POST_WORKFLOW_STEP_THREAD',
    callback,
    meta: { workflowId: normalizedWorkflowId, stepId: normalizedStepId }
  });
};

// The Test window's own chat: one thread per workflow, apart from the workflow's thread, so what is said about a
// test never lands in the build conversation.
export const getOrCreateWorkflowTestThread = ({ workflowId } = {}) => {
  const normalizedWorkflowId = String(workflowId || '').trim();

  return post(`/v1/pieve/workflows/${encodeURIComponent(normalizedWorkflowId)}/test-thread`, {
    action: 'POST_WORKFLOW_TEST_THREAD',
    meta: { workflowId: normalizedWorkflowId }
  });
};

// Every step task on this workflow in one read, so the canvas can light its rows.
export const getWorkflowStepTasks = ({ workflowId } = {}) => {
  const normalizedWorkflowId = String(workflowId || '').trim();

  return get(`/v1/pieve/workflows/${encodeURIComponent(normalizedWorkflowId)}/step-tasks`, {
    action: 'GET_WORKFLOW_STEP_TASKS',
    meta: { workflowId: normalizedWorkflowId }
  });
};
